import { useState, useRef } from "react";
import { Spin, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { api } from "../../services/api";
import { resolveImgUrl } from "../../templates/4/utils";
import { T, TInput } from "./editorAtoms";

export const ICON_PRESETS = ["📦", "🔒", "💳", "🚚", "⚡", "✅", "🎯", "🌟", "🎁", "💬", "📍", "🛡", "🔥", "💎", "🏆", "❤️"];

// Selector unificado de ícono/imagen: un solo cuadrito que abre un menú
// (Ícono → paleta de emojis, o Imagen → subir archivo). Hover muestra el
// tacho para limpiar. `item` usa { icon, image } y `onUpdate` recibe un patch.
// Viene de techdrop sin los íconos de marca, que allá usa el pie de página.
export function ItemMediaSlot({ item, onUpdate, size = 64 }) {
  const [hover, setHover] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [haciaArriba, setHaciaArriba] = useState(false);
  const slotRef = useRef(null);
  const fileRef = useRef(null);

  // Si no hay lugar abajo (típico cuando el ítem está al final del panel), el
  // menú se abre hacia arriba para que no quede cortado.
  const abrirMenu = (abrir) => {
    const r = slotRef.current?.getBoundingClientRect();
    if (r) setHaciaArriba(window.innerHeight - r.bottom < 230);
    abrir();
  };
  // Posición vertical común a los tres desplegables.
  const posMenu = haciaArriba
    ? { bottom: "100%", marginBottom: 4 }
    : { top: "100%", marginTop: 4 };
  const hasContent = !!(item.image || item.icon);
  const imgUrl = resolveImgUrl(item.image);

  async function handleFile(file) {
    if (!file) return;
    setUploading(true);
    try {
      const res = await api.tienda.uploadImagen(file);
      if (res.status === "ok") onUpdate({ image: res.url, icon: "" });
      else message.error(res.error || "Error al subir imagen");
    } catch { message.error("Error de conexión"); }
    finally { setUploading(false); }
  }

  function clear() {
    onUpdate({ image: null, icon: "" });
  }

  const closeAll = () => { setMenuOpen(false); setEmojiOpen(false); };

  return (
    <div ref={slotRef} style={{ position: "relative", width: size }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div
        onClick={() => { if (!hasContent && !uploading) abrirMenu(() => setMenuOpen(m => !m)); }}
        style={{
          width: size, height: size, borderRadius: T.rs, overflow: "hidden",
          border: `1.5px dashed ${T.bdr}`, background: "#fafbfe",
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: hasContent ? "default" : "pointer",
        }}>
        {uploading ? <Spin size="small" /> :
          item.image ? <img src={imgUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> :
          item.icon ? <span style={{ fontSize: size * 0.44 }}>{item.icon}</span> :
          <span style={{ color: T.mut, fontSize: 22 }}>+</span>}
      </div>

      {hasContent && hover && !uploading && (
        <button onClick={clear}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%",
            background: "rgba(0,0,0,0.45)", border: "none", borderRadius: T.rs,
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          }}>
          <span style={{ color: "#ff5c5c", fontSize: 18 }}><DeleteOutlined/></span>
        </button>
      )}

      <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
        onChange={e => { const f = e.target.files[0]; e.target.value = ""; if (f) handleFile(f); }} />

      {(menuOpen || emojiOpen) && (
        <div onClick={closeAll} style={{ position: "fixed", inset: 0, zIndex: 19 }} />
      )}

      {menuOpen && (
        <div style={{
          position: "absolute", ...posMenu, left: 0, zIndex: 20,
          background: "#fff", border: `1px solid ${T.bdr}`, borderRadius: T.rs,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)", width: 150, overflow: "hidden",
        }}>
          <button onClick={() => { setMenuOpen(false); setEmojiOpen(true); }}
            style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", fontSize: 12, background: "none", border: "none", cursor: "pointer", color: T.txt }}>
            🙂 Ícono
          </button>
          <button onClick={() => { setMenuOpen(false); fileRef.current?.click(); }}
            style={{ display: "block", width: "100%", textAlign: "left", padding: "8px 10px", fontSize: 12, background: "none", border: "none", cursor: "pointer", color: T.txt, borderTop: `1px solid ${T.bdr}` }}>
            🖼 Imagen
          </button>
        </div>
      )}

      {emojiOpen && (
        <div style={{
          position: "absolute", ...posMenu, left: 0, zIndex: 20,
          background: "#fff", border: `1px solid ${T.bdr}`, borderRadius: T.rs,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)", padding: 8, width: 168,
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
            {ICON_PRESETS.map(e => (
              <button key={e} onClick={() => { onUpdate({ icon: e, image: null }); setEmojiOpen(false); }}
                style={{ fontSize: 18, padding: 4, background: "none", border: "none", cursor: "pointer", borderRadius: 4 }}
                onMouseEnter={ev => ev.currentTarget.style.background = "#f0f2f8"}
                onMouseLeave={ev => ev.currentTarget.style.background = "none"}>
                {e}
              </button>
            ))}
          </div>
          <div style={{ marginTop: 6 }}>
            <TInput value={item.icon || ""} onChange={v => onUpdate({ icon: v })} placeholder="Otro emoji..." />
          </div>
        </div>
      )}
    </div>
  );
}
